import { useEffect, useState, useCallback, useMemo, type ReactNode } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Alert,
  Stack,
  Tooltip,
  Divider,
  CircularProgress,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  Send as SendIcon,
  LocalShipping as ShipmentIcon,
  Router as GatewayIcon,
  Hub as MatrixIcon,
  Sensors as NodeIcon,
  Satellite as SatelliteIcon,
  QrCodeScanner as BarcodeIcon,
  CameraAlt as CameraIcon,
  Devices as DeviceIcon,
} from '@mui/icons-material';
import { EnhancedTable, type TableColumn, type TableGroupHeaderContext } from '../../components/ui/EnhancedTable';
import { AppButton, AppIconButton, AppListPage, AppTextField } from '../../components/design-system';
import { tenantDevicesService } from '../../services/TenantDevicesService';
import { useAppToolbar } from '../../contexts/AppToolbarContext';
import type { PendingShipmentSummary, TenantDeviceRecord } from '@cnapse/shared/schemas.js';

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'primary' | 'success' | 'error' | 'info'> = {
  'in-stock': 'default',
  allocated: 'warning',
  shipped: 'primary',
  claimed: 'info',
  active: 'success',
  returned: 'warning',
  retired: 'error',
};

const STATUS_LABELS: Record<string, string> = {
  'in-stock': 'In Stock',
  allocated: 'Allocated',
  shipped: 'Shipped',
  claimed: 'Claimed',
  active: 'Active',
  returned: 'Returned',
  retired: 'Retired',
};

import { getDeviceCategoryLabel, getDeviceSkuCatalogEntry } from '@cnapse/shared';
function formatDate(d: string | null | undefined): string {
  if (!d) return '—';
  return new Date(d).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

const DEVICE_CATEGORY_ICONS: Record<string, ReactNode> = {
  'efem-gateway': <GatewayIcon fontSize="small" />,
  'location-gateway': <MatrixIcon fontSize="small" />,
  'location-tag': <NodeIcon fontSize="small" />,
  'location-anchor': <SatelliteIcon fontSize="small" />,
  'barcode-scanner': <BarcodeIcon fontSize="small" />,
  'handheld-barcode-scanner': <BarcodeIcon fontSize="small" />,
  'waferid-camera': <CameraIcon fontSize="small" />,
};

function renderDeviceGroupHeader({ groupKey, label, count }: TableGroupHeaderContext) {
  return (
    <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
      <Box sx={{ color: 'primary.main', display: 'flex', alignItems: 'center' }}>
        {DEVICE_CATEGORY_ICONS[groupKey] ?? <DeviceIcon fontSize="small" />}
      </Box>
      <Typography variant="subtitle2" fontWeight={600} noWrap>
        {label}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        · {count} device{count === 1 ? '' : 's'}
      </Typography>
    </Stack>
  );
}

function buildColumns(onCopy: (text: string) => void): TableColumn<TenantDeviceRecord>[] {
  return [
    {
      field: 'serialNumber',
      headerName: 'Serial Number',
      minWidth: 180,
      align: 'left',
      renderCell: (row) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
            {row.serialNumber}
          </Typography>
          <Tooltip title="Copy">
            <AppIconButton size="small" onClick={() => onCopy(row.serialNumber)} sx={{ opacity: 0.4, '&:hover': { opacity: 1 } }} aria-label="Copy serial">
              <CopyIcon sx={{ fontSize: 13 }} />
            </AppIconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'sku',
      headerName: 'SKU',
      minWidth: 120,
      align: 'left',
      renderCell: (row) => (
        <Typography variant="body2" fontFamily="monospace">
          {getDeviceSkuCatalogEntry(row.deviceCategory)?.sku ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'orderReference',
      headerName: 'Order',
      minWidth: 150,
      align: 'left',
      renderCell: (row) => (
        <Typography variant="body2" fontFamily="monospace" color="text.secondary">
          {row.orderReference ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'inventoryStatus',
      headerName: 'Status',
      minWidth: 120,
      align: 'center',
      renderCell: (row) => (
        <Chip
          label={STATUS_LABELS[row.inventoryStatus ?? ''] ?? row.inventoryStatus ?? '—'}
          size="small"
          color={STATUS_COLORS[row.inventoryStatus ?? ''] ?? 'default'}
        />
      ),
    },
  ];
}

export default function DeviceClaims() {
  const [devices, setDevices] = useState<TenantDeviceRecord[]>([]);
  const [shipments, setShipments] = useState<PendingShipmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState('');
  const [claimError, setClaimError] = useState<string | null>(null);
  const [claimingOrder, setClaimingOrder] = useState<string | null>(null);
  const [claimCodes, setClaimCodes] = useState<Record<string, string>>({});

  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [devicesRes, shipmentsRes] = await Promise.all([
        tenantDevicesService.getTenantDevices(),
        tenantDevicesService.getPendingShipments(),
      ]);
      setDevices(devicesRes.data.devices);
      setShipments(shipmentsRes.data.shipments);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCopy = useCallback((text: string) => {
    void navigator.clipboard.writeText(text);
  }, []);

  const handleClaim = async (orderReference: string) => {
    const claimCode = claimCodes[orderReference]?.trim();    
    if (!claimCode) return;

    setClaimingOrder(orderReference);
    setClaimError(null);
    try {
      const res = await tenantDevicesService.claimOrder({ claimCode });
      setSuccessMsg(`Shipment ${res.data.order.orderReference} claimed for ${res.data.devicesClaimed} device${res.data.devicesClaimed === 1 ? '' : 's'}.`);
      setClaimCodes((current) => ({ ...current, [orderReference]: '' }));
      void load();
    } catch (e) {
      setClaimError(e instanceof Error ? e.message : 'Claim failed');
    } finally {
      setClaimingOrder(null);
    }
  };

  const filteredDevices = useMemo(() => devices.filter((d) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      d.serialNumber.toLowerCase().includes(q) ||
      (getDeviceSkuCatalogEntry(d.deviceCategory)?.sku ?? '').toLowerCase().includes(q) ||
      (d.orderReference ?? '').toLowerCase().includes(q) ||
      getDeviceCategoryLabel(d.deviceCategory).toLowerCase().includes(q) ||
      (d.inventoryStatus ?? '').toLowerCase().includes(q)
    );
  }), [devices, search]);

  const sortedDevices = useMemo(
    () => [...filteredDevices].sort((a, b) => a.serialNumber.localeCompare(b.serialNumber)),
    [filteredDevices],
  );

  const columns = buildColumns(handleCopy);

  const toolbarPageActions = useMemo(
    () => (
      <Tooltip title="Refresh">
        <span>
          <AppIconButton onClick={load} disabled={loading} aria-label="Refresh devices">
            {loading ? <CircularProgress size={20} /> : <RefreshIcon />}
          </AppIconButton>
        </span>
      </Tooltip>
    ),
    [load, loading],
  );

  useAppToolbar({ pageActions: toolbarPageActions });

  return (
    <AppListPage>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {claimError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setClaimError(null)}>{claimError}</Alert>}
      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg('')}>{successMsg}</Alert>}

      {shipments.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2.5, mb: 2 }}>
          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <ShipmentIcon color="primary" fontSize="small" />
            <Typography variant="h6">Pending Shipments</Typography>
            <Chip label={shipments.length} size="small" color="primary" />
          </Box>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Enter the shipment claim code you received.
          </Typography>

          <Stack spacing={2}>
            {shipments.map((shipment) => (
              <Paper key={shipment.orderReference} variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Stack spacing={1.5}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" gap={2}>
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold" fontFamily="monospace">
                        {shipment.orderReference}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Shipped: {formatDate(shipment.actualShipDate ?? shipment.expectedShipDate)}
                      </Typography>
                    </Box>
                    <Chip label={`${shipment.deviceCount} device${shipment.deviceCount === 1 ? '' : 's'}`} size="small" />
                  </Box>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {shipment.deviceSummary.map((item) => (
                      <Chip
                        key={`${shipment.orderReference}-${item.category}`}
                        label={`${getDeviceCategoryLabel(item.category)}: ${item.count}`}
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
                    <AppTextField
                      label="Claim Code"
                      size="small"
                      value={claimCodes[shipment.orderReference] ?? ''}
                      onChange={(e) => setClaimCodes((current) => ({ ...current, [shipment.orderReference]: e.target.value.toUpperCase() }))}
                      placeholder="ABCD-1234"
                      sx={{ flex: 1 }}
                    />
                    <AppButton
                      variant="contained"
                      onClick={() => void handleClaim(shipment.orderReference)}
                      disabled={claimingOrder === shipment.orderReference || !(claimCodes[shipment.orderReference]?.trim())}
                      startIcon={claimingOrder === shipment.orderReference ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
                    >
                      Claim Shipment
                    </AppButton>
                  </Stack>
                </Stack>
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}

      {shipments.length > 0 ? <Divider sx={{ mb: 2 }} /> : null}

      <EnhancedTable<TenantDeviceRecord>
        data={sortedDevices}
        totalCount={sortedDevices.length}
        columns={columns}
        loading={loading}
        page={1}
        pageSize={sortedDevices.length || 1}
        onPageChange={() => {}}
        onPageSizeChange={() => {}}
        getRowId={(r) => r.serialNumber}
        hidePagination
        searchable
        searchTerm={search}
        searchPlaceholder="Search by serial, SKU, order or status…"
        onSearchChange={setSearch}
        groupBy={(row) => row.deviceCategory}
        getGroupLabel={getDeviceCategoryLabel}
        groupDisplayMode="section-header"
        renderGroupHeader={renderDeviceGroupHeader}
        emptyStateTitle="No devices found"
        emptyStateDescription={
          search
            ? 'No devices match your search.'
            : 'No devices have been claimed for this tenant yet.'
        }
        maxHeight="calc(100vh - 220px)"
      />
    </AppListPage>
  );
}
